import React from 'react';
import { FiZap } from 'react-icons/fi';
import LessonTemplate from './components/LessonTemplate';
import QuantLessonPracticeSlot from './components/QuantLessonPracticeSlot';

// ============================================
// VISUAL COMPONENTS FOR MATH SHORTCUTS
// Using Unicode symbols for visual display
// ============================================

// Multiplication Shortcuts Visual
const MultiplicationShortcutsVisual = () => (
  <div
    style={{
      padding: '1.5rem',
      background: 'var(--liwaza-surface-container, #f8fafc)',
      borderRadius: '12px',
      border: '2px solid var(--liwaza-primary, #6366f1)',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1.5rem',
        color: 'var(--liwaza-primary, #6366f1)',
        fontSize: '1.3rem',
        fontWeight: '600',
      }}
    >
      Speed Multiplication Tricks
    </h4>
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '1rem',
      }}
    >
      <div
        style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}
      >
        <h5
          style={{
            marginBottom: '0.5rem',
            color: '#6366f1',
            fontWeight: '600',
          }}
        >
          ×11 Trick
        </h5>
        <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: '#374151' }}>
          <strong>45 × 11:</strong>
          <br />
          Split: 4_5 → Put sum in middle
          <br />4 + 5 = 9 → <strong style={{ color: '#059669' }}>495</strong>
        </p>
      </div>
      <div
        style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}
      >
        <h5
          style={{
            marginBottom: '0.5rem',
            color: '#6366f1',
            fontWeight: '600',
          }}
        >
          ×5 Trick
        </h5>
        <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: '#374151' }}>
          <strong>48 × 5:</strong>
          <br />
          Divide by 2, multiply by 10
          <br />
          48 ÷ 2 = 24 → <strong style={{ color: '#059669' }}>240</strong>
        </p>
      </div>
      <div
        style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}
      >
        <h5
          style={{
            marginBottom: '0.5rem',
            color: '#6366f1',
            fontWeight: '600',
          }}
        >
          ×25 Trick
        </h5>
        <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: '#374151' }}>
          <strong>36 × 25:</strong>
          <br />
          Divide by 4, multiply by 100
          <br />
          36 ÷ 4 = 9 → <strong style={{ color: '#059669' }}>900</strong>
        </p>
      </div>
      <div
        style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}
      >
        <h5
          style={{
            marginBottom: '0.5rem',
            color: '#6366f1',
            fontWeight: '600',
          }}
        >
          ×9 Trick
        </h5>
        <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: '#374151' }}>
          <strong>47 × 9:</strong>
          <br />
          ×10 then subtract the number
          <br />
          470 − 47 = <strong style={{ color: '#059669' }}>423</strong>
        </p>
      </div>
    </div>
  </div>
);

// Squaring Shortcuts Visual
const SquaringShortcutsVisual = () => (
  <div
    style={{
      padding: '1.5rem',
      background: 'var(--liwaza-surface-container, #f8fafc)',
      borderRadius: '12px',
      border: '2px solid #059669',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1.5rem',
        color: '#059669',
        fontSize: '1.3rem',
        fontWeight: '600',
      }}
    >
      Perfect Square Shortcuts
    </h4>
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '1rem',
      }}
    >
      <div
        style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}
      >
        <h5
          style={{
            marginBottom: '0.5rem',
            color: '#059669',
            fontWeight: '600',
          }}
        >
          Numbers ending in 5
        </h5>
        <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: '#374151' }}>
          <strong>
            35<sup>2</sup>:
          </strong>
          <br />
          n × (n+1), then append 25
          <br />3 × 4 = 12 → <strong style={{ color: '#6366f1' }}>1225</strong>
        </p>
      </div>
      <div
        style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}
      >
        <h5
          style={{
            marginBottom: '0.5rem',
            color: '#059669',
            fontWeight: '600',
          }}
        >
          Near 50: (50±n)<sup>2</sup>
        </h5>
        <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: '#374151' }}>
          <strong>
            48<sup>2</sup> = (50−2)<sup>2</sup>:
          </strong>
          <br />
          (25−2)×100 + 2<sup>2</sup> = 2300 + 4
          <br />= <strong style={{ color: '#6366f1' }}>2304</strong>
        </p>
      </div>
      <div
        style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}
      >
        <h5
          style={{
            marginBottom: '0.5rem',
            color: '#059669',
            fontWeight: '600',
          }}
        >
          Near 100: (100±n)<sup>2</sup>
        </h5>
        <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: '#374151' }}>
          <strong>
            97<sup>2</sup> = (100−3)<sup>2</sup>:
          </strong>
          <br />
          (97−3)×100 + 3<sup>2</sup> = 9400 + 9
          <br />= <strong style={{ color: '#6366f1' }}>9409</strong>
        </p>
      </div>
      <div
        style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}
      >
        <h5
          style={{
            marginBottom: '0.5rem',
            color: '#059669',
            fontWeight: '600',
          }}
        >
          (a+b)(a−b) = a<sup>2</sup> − b<sup>2</sup>
        </h5>
        <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: '#374151' }}>
          <strong>43 × 37:</strong>
          <br />= (40+3)(40−3) = 40<sup>2</sup> − 3<sup>2</sup>
          <br />= 1600 − 9 = <strong style={{ color: '#6366f1' }}>1591</strong>
        </p>
      </div>
    </div>
  </div>
);

// Fraction & Percentage Shortcuts Visual
const FractionPercentageVisual = () => (
  <div
    style={{
      padding: '1.5rem',
      background: 'var(--liwaza-surface-container, #f8fafc)',
      borderRadius: '12px',
      border: '2px solid #f59e0b',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1.5rem',
        color: '#f59e0b',
        fontSize: '1.3rem',
        fontWeight: '600',
      }}
    >
      Fraction-Percent Conversions
    </h4>
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '0.75rem',
        marginBottom: '1rem',
      }}
    >
      {[
        { frac: '1/2', pct: '50%' },
        { frac: '1/3', pct: '33.3%' },
        { frac: '1/4', pct: '25%' },
        { frac: '1/5', pct: '20%' },
        { frac: '1/6', pct: '16.7%' },
        { frac: '1/8', pct: '12.5%' },
        { frac: '2/3', pct: '66.7%' },
        { frac: '3/4', pct: '75%' },
        { frac: '2/5', pct: '40%' },
        { frac: '3/5', pct: '60%' },
        { frac: '4/5', pct: '80%' },
        { frac: '5/6', pct: '83.3%' },
      ].map((item, idx) => (
        <div
          key={idx}
          style={{
            background: 'white',
            padding: '0.5rem',
            borderRadius: '8px',
            textAlign: 'center',
            border: '1px solid #e5e7eb',
          }}
        >
          <div
            style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#374151' }}
          >
            {item.frac}
          </div>
          <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
            {item.pct}
          </div>
        </div>
      ))}
    </div>
    <div
      style={{
        background: 'white',
        padding: '1rem',
        borderRadius: '8px',
        textAlign: 'center',
        border: '1px solid #e5e7eb',
      }}
    >
      <strong style={{ color: '#374151' }}>Quick Tip:</strong>{' '}
      <span style={{ color: '#6b7280' }}>
        To find 15% of 80, use 10% + 5%: 8 + 4 ={' '}
        <strong style={{ color: '#059669' }}>12</strong>
      </span>
    </div>
  </div>
);

// Estimation Techniques Visual
const EstimationVisual = () => (
  <div
    style={{
      padding: '1.5rem',
      background: 'var(--liwaza-surface-container, #f8fafc)',
      borderRadius: '12px',
      border: '2px solid #3b82f6',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1.5rem',
        color: '#3b82f6',
        fontSize: '1.3rem',
        fontWeight: '600',
      }}
    >
      Strategic Estimation
    </h4>
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '1rem',
      }}
    >
      <div
        style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}
      >
        <h5
          style={{
            marginBottom: '0.5rem',
            color: '#3b82f6',
            fontWeight: '600',
          }}
        >
          Round and Adjust
        </h5>
        <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: '#374151' }}>
          <strong>48 × 52:</strong>
          <br />
          ≈ 50 × 50 = 2500
          <br />
          Actual: <strong style={{ color: '#059669' }}>2496</strong> (very
          close!)
        </p>
      </div>
      <div
        style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}
      >
        <h5
          style={{
            marginBottom: '0.5rem',
            color: '#3b82f6',
            fontWeight: '600',
          }}
        >
          Use Benchmarks
        </h5>
        <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: '#374151' }}>
          <strong>√50 ≈ ?</strong>
          <br />
          √49 = 7, √64 = 8
          <br />
          50 is close to 49 → ≈{' '}
          <strong style={{ color: '#059669' }}>7.1</strong>
        </p>
      </div>
      <div
        style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}
      >
        <h5
          style={{
            marginBottom: '0.5rem',
            color: '#3b82f6',
            fontWeight: '600',
          }}
        >
          Eliminate Options
        </h5>
        <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: '#374151' }}>
          <strong>123 × 47:</strong>
          <br />
          ≈ 120 × 50 = 6000
          <br />
          Eliminate answers far from 6000
        </p>
      </div>
      <div
        style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}
      >
        <h5
          style={{
            marginBottom: '0.5rem',
            color: '#3b82f6',
            fontWeight: '600',
          }}
        >
          Last Digit Check
        </h5>
        <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: '#374151' }}>
          <strong>37 × 43 = ?</strong>
          <br />
          7 × 3 = 21, ends in 1
          <br />
          Only choose answers ending in{' '}
          <strong style={{ color: '#059669' }}>1</strong>
        </p>
      </div>
    </div>
  </div>
);

// Division Shortcuts Visual
const DivisionShortcutsVisual = () => (
  <div
    style={{
      padding: '1.5rem',
      background: 'var(--liwaza-surface-container, #f8fafc)',
      borderRadius: '12px',
      border: '2px solid #ec4899',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1.5rem',
        color: '#ec4899',
        fontSize: '1.3rem',
        fontWeight: '600',
      }}
    >
      Division Speed Tricks
    </h4>
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '1rem',
      }}
    >
      <div
        style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}
      >
        <h5
          style={{
            marginBottom: '0.5rem',
            color: '#ec4899',
            fontWeight: '600',
          }}
        >
          ÷5 Trick
        </h5>
        <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: '#374151' }}>
          <strong>230 ÷ 5:</strong>
          <br />
          Multiply by 2, divide by 10
          <br />
          230 × 2 = 460 ÷ 10 = <strong style={{ color: '#059669' }}>46</strong>
        </p>
      </div>
      <div
        style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}
      >
        <h5
          style={{
            marginBottom: '0.5rem',
            color: '#ec4899',
            fontWeight: '600',
          }}
        >
          ÷25 Trick
        </h5>
        <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: '#374151' }}>
          <strong>450 ÷ 25:</strong>
          <br />
          Multiply by 4, divide by 100
          <br />
          450 × 4 = 1800 ÷ 100 ={' '}
          <strong style={{ color: '#059669' }}>18</strong>
        </p>
      </div>
      <div
        style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}
      >
        <h5
          style={{
            marginBottom: '0.5rem',
            color: '#ec4899',
            fontWeight: '600',
          }}
        >
          ÷8 Trick
        </h5>
        <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: '#374151' }}>
          <strong>248 ÷ 8:</strong>
          <br />
          Divide by 2 three times
          <br />
          248 → 124 → 62 → <strong style={{ color: '#059669' }}>31</strong>
        </p>
      </div>
      <div
        style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}
      >
        <h5
          style={{
            marginBottom: '0.5rem',
            color: '#ec4899',
            fontWeight: '600',
          }}
        >
          Divisibility by 9
        </h5>
        <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: '#374151' }}>
          <strong>738 ÷ 9:</strong>
          <br />
          Digit sum: 7+3+8=18, 18÷9=2
          <br />
          738 is divisible by 9 →{' '}
          <strong style={{ color: '#059669' }}>82</strong>
        </p>
      </div>
    </div>
  </div>
);

// Key Numbers to Memorize Visual
const KeyNumbersVisual = () => (
  <div
    style={{
      padding: '1.5rem',
      background: 'var(--liwaza-surface-container, #f8fafc)',
      borderRadius: '12px',
      border: '2px solid #8b5cf6',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1.5rem',
        color: '#8b5cf6',
        fontSize: '1.3rem',
        fontWeight: '600',
      }}
    >
      Essential Numbers to Memorize
    </h4>
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '1rem',
      }}
    >
      <div
        style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}
      >
        <h5
          style={{
            marginBottom: '0.5rem',
            color: '#8b5cf6',
            fontWeight: '600',
          }}
        >
          Perfect Squares
        </h5>
        <p style={{ fontSize: '0.85rem', lineHeight: 1.8, color: '#374151' }}>
          1² = 1, 2² = 4
          <br />
          3² = 9, 4² = 16
          <br />
          5² = 25, 6² = 36
          <br />
          7² = 49, 8² = 64
          <br />
          9² = 81, 10² = 100
          <br />
          11² = 121, 12² = 144
          <br />
          13² = 169, 15² = 225
          <br />
          20² = 400, 25² = 625
        </p>
      </div>
      <div
        style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}
      >
        <h5
          style={{
            marginBottom: '0.5rem',
            color: '#8b5cf6',
            fontWeight: '600',
          }}
        >
          Perfect Cubes
        </h5>
        <p style={{ fontSize: '0.85rem', lineHeight: 1.8, color: '#374151' }}>
          1³ = 1
          <br />
          2³ = 8
          <br />
          3³ = 27
          <br />
          4³ = 64
          <br />
          5³ = 125
          <br />
          6³ = 216
          <br />
          7³ = 343
          <br />
          10³ = 1000
        </p>
      </div>
      <div
        style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}
      >
        <h5
          style={{
            marginBottom: '0.5rem',
            color: '#8b5cf6',
            fontWeight: '600',
          }}
        >
          Powers of 2
        </h5>
        <p style={{ fontSize: '0.85rem', lineHeight: 1.8, color: '#374151' }}>
          2¹ = 2, 2² = 4
          <br />
          2³ = 8, 2⁴ = 16
          <br />
          2⁵ = 32, 2⁶ = 64
          <br />
          2⁷ = 128, 2⁸ = 256
          <br />
          2⁹ = 512, 2¹⁰ = 1024
          <br />
          <br />
          <strong>Tip:</strong> Know these for exponent problems!
        </p>
      </div>
    </div>
  </div>
);

// Algebraic Shortcuts Visual
const AlgebraShortcutsVisual = () => (
  <div
    style={{
      padding: '1.5rem',
      background: 'var(--liwaza-surface-container, #f8fafc)',
      borderRadius: '12px',
      border: '2px solid #6366f1',
    }}
  >
    <h4
      style={{
        textAlign: 'center',
        marginBottom: '1.5rem',
        color: '#6366f1',
        fontSize: '1.3rem',
        fontWeight: '600',
      }}
    >
      Algebraic Speed Formulas
    </h4>
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '1rem',
      }}
    >
      <div
        style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}
      >
        <h5
          style={{
            marginBottom: '0.5rem',
            color: '#6366f1',
            fontWeight: '600',
          }}
        >
          (a + b)² = a² + 2ab + b²
        </h5>
        <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: '#374151' }}>
          <strong>
            Example: 23<sup>2</sup>
          </strong>
          <br />
          = (20 + 3)² = 400 + 120 + 9
          <br />= <strong style={{ color: '#059669' }}>529</strong>
        </p>
      </div>
      <div
        style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}
      >
        <h5
          style={{
            marginBottom: '0.5rem',
            color: '#6366f1',
            fontWeight: '600',
          }}
        >
          (a − b)² = a² − 2ab + b²
        </h5>
        <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: '#374151' }}>
          <strong>
            Example: 29<sup>2</sup>
          </strong>
          <br />
          = (30 − 1)² = 900 − 60 + 1
          <br />= <strong style={{ color: '#059669' }}>841</strong>
        </p>
      </div>
      <div
        style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}
      >
        <h5
          style={{
            marginBottom: '0.5rem',
            color: '#6366f1',
            fontWeight: '600',
          }}
        >
          a² − b² = (a+b)(a−b)
        </h5>
        <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: '#374151' }}>
          <strong>
            Example: 53<sup>2</sup> − 47<sup>2</sup>
          </strong>
          <br />
          = (53+47)(53−47) = 100 × 6
          <br />= <strong style={{ color: '#059669' }}>600</strong>
        </p>
      </div>
      <div
        style={{
          background: 'white',
          padding: '1rem',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}
      >
        <h5
          style={{
            marginBottom: '0.5rem',
            color: '#6366f1',
            fontWeight: '600',
          }}
        >
          (a+b)³ expansion
        </h5>
        <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: '#374151' }}>
          = a³ + 3a²b + 3ab² + b³
          <br />
          <strong>Pattern:</strong> Coefficients are 1, 3, 3, 1
        </p>
      </div>
    </div>
  </div>
);

const MathShortcutsLesson = () => {
  const sections = [
    {
      id: 'multiplication-tricks',
      title: 'Multiplication Shortcuts',
      content: {
        introduction:
          'Mastering multiplication shortcuts can save precious minutes on the GRE. These techniques transform complex calculations into simple mental math.',

        concepts: [
          {
            title: 'Essential Multiplication Tricks',
            explanation:
              'These tricks work because of underlying mathematical properties:\n\n• ×11 trick: Uses the digit sum pattern because §11 = 10 + 1§\n• ×5 trick: §n × 5 = n ÷ 2 × 10§ (since §5 = 10/2§)\n• ×25 trick: §n × 25 = n ÷ 4 × 100§ (since §25 = 100/4§)\n• ×9 trick: §n × 9 = n × 10 - n§ (since §9 = 10 - 1§)\n\nPractice these until they become automatic!',
            visual: <MultiplicationShortcutsVisual />,
            example: {
              problem: 'Calculate §67 × 11§ using the shortcut',
              solution: [
                'Split the digits: §6\\_7§',
                'Add the digits: §6 + 7 = 13§',
                'Since sum > 9, carry the 1',
                'Result: §6§, §(1+6)§, §3§, §7§ = §737§',
                'Verify: §67 × 11 = 67 × 10 + 67 = 670 + 67 = 737§ ✓',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'squaring-tricks',
      title: 'Squaring Numbers',
      content: {
        introduction:
          'Squaring numbers quickly is essential for geometry, algebra, and data analysis questions. Learn these patterns to calculate squares in seconds.',

        concepts: [
          {
            title: 'Quick Squaring Techniques',
            explanation:
              'Numbers ending in 5:\nFor any number §n5§, we have §(n5)^2 = n(n+1)§ followed by §25§.\nExample: §35^2§ → §3 × 4 = 12§, append §25§ → §1225§\n\nNumbers near 50:\n§(50 ± n)^2 = (25 ± n) × 100 + n^2§\nExample: §48^2 = (50-2)^2 = 23 × 100 + 4 = 2304§\n\nNumbers near 100:\n§(100 ± n)^2 = (100 ± 2n) × 100 + n^2§\nExample: §97^2 = 94 × 100 + 9 = 9409§',
            visual: <SquaringShortcutsVisual />,
            example: {
              problem: 'Calculate §65^2§ using the shortcut',
              solution: [
                'For numbers ending in 5: §n × (n+1)§, then append 25',
                '§65 = 6\\_5§, so §n = 6§',
                '§6 × 7 = 42§',
                'Append 25: §4225§',
                '§65^2 = 4225§ ✓',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'division-tricks',
      title: 'Division Shortcuts',
      content: {
        introduction:
          'Division can be slow, but these shortcuts turn division problems into easier multiplication problems. The key is recognizing patterns.',

        concepts: [
          {
            title: 'Speed Division Techniques',
            explanation:
              'The key insight is that dividing by a number is the same as multiplying by its reciprocal:\n\n• ÷5: §n ÷ 5 = n × 2 ÷ 10§ (since §1/5 = 2/10§)\n• ÷25: §n ÷ 25 = n × 4 ÷ 100§ (since §1/25 = 4/100§)\n• ÷8: Divide by 2 three times (§8 = 2^3§)\n• ÷9: Check divisibility by digit sum first\n\nAlso use divisibility rules to check if division is exact before calculating.',
            visual: <DivisionShortcutsVisual />,
            example: {
              problem: 'Calculate §875 ÷ 25§ using the shortcut',
              solution: [
                'Use the ÷25 trick: multiply by 4, divide by 100',
                '§875 × 4 = 3500§',
                '§3500 ÷ 100 = 35§',
                'Therefore, §875 ÷ 25 = 35§ ✓',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'fractions-percentages',
      title: 'Fractions & Percentages',
      content: {
        introduction:
          'Quick conversions between fractions, decimals, and percentages are crucial for GRE success. Memorizing common conversions saves valuable time.',

        concepts: [
          {
            title: 'Essential Conversions',
            explanation:
              'Memorize these common fraction-percent conversions:\n\nHalves and quarters: §1/2 = 50\\%§, §1/4 = 25\\%§, §3/4 = 75\\%§\n\nThirds: §1/3 ≈ 33.3\\%§, §2/3 ≈ 66.7\\%§\n\nFifths: §1/5 = 20\\%§, §2/5 = 40\\%§, §3/5 = 60\\%§, §4/5 = 80\\%§\n\nEighths: §1/8 = 12.5\\%§, §3/8 = 37.5\\%§, §5/8 = 62.5\\%§, §7/8 = 87.5\\%§\n\nQuick tip: For complex percentages, break them into parts: §15\\% = 10\\% + 5\\%§',
            visual: <FractionPercentageVisual />,
            example: {
              problem: 'Find §35\\%§ of §240§ quickly',
              solution: [
                'Break §35\\%§ into §25\\% + 10\\%§',
                '§25\\%§ of §240 = 1/4 × 240 = 60§',
                '§10\\%§ of §240 = 24§',
                '§35\\% = 60 + 24 = 84§',
                'Or: §35\\% = 7/20§, so §240 × 7 ÷ 20 = 1680 ÷ 20 = 84§ ✓',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'estimation-techniques',
      title: 'Strategic Estimation',
      content: {
        introduction:
          "On the GRE, you often don't need exact answers. Strategic estimation helps you eliminate wrong answers quickly and verify calculations.",

        concepts: [
          {
            title: 'Estimation Strategies',
            explanation:
              "Key estimation strategies include:\n\n• Round to nice numbers: §48 × 52 ≈ 50 × 50 = 2500§ (actual: 2496)\n\n• Use benchmarks: §\\sqrt{50}§ is between §\\sqrt{49} = 7§ and §\\sqrt{64} = 8§, closer to 7, so ≈ 7.1\n\n• Last digit analysis: Check if your answer's last digit matches\n\n• Order of magnitude: Is the answer in hundreds, thousands, etc.?\n\nEstimation is especially powerful for Quantitative Comparison questions!",
            visual: <EstimationVisual />,
            example: {
              problem:
                'Estimate: §487 × 23§. Options: A) 9,201  B) 11,201  C) 12,401  D) 8,201',
              solution: [
                'Round: §487 ≈ 500§, §23 ≈ 20§',
                'Estimate: §500 × 20 = 10,000§',
                'B (11,201) is closest to 10,000',
                'Verify last digit: §7 × 3 = 21§, ends in 1',
                'Both A and B end in 1, but B is closer to estimate ✓',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'key-numbers',
      title: 'Numbers to Memorize',
      content: {
        introduction:
          'Having key numbers memorized eliminates calculation time and reduces errors. These numbers appear frequently on the GRE.',

        concepts: [
          {
            title: 'Essential Numbers Reference',
            explanation:
              'Perfect squares through §25^2§ and cubes through §10^3§ are essential:\n\nPerfect Squares: §1, 4, 9, 16, 25, 36, 49, 64, 81, 100, 121, 144, 169, 196, 225...§\n\nPerfect Cubes: §1, 8, 27, 64, 125, 216, 343, 512, 729, 1000§\n\nPowers of 2: §2, 4, 8, 16, 32, 64, 128, 256, 512, 1024§\n\nKnowing these by heart allows you to:\n• Recognize patterns instantly\n• Simplify square roots quickly\n• Verify calculations\n• Identify perfect squares/cubes in problems',
            visual: <KeyNumbersVisual />,
            example: {
              problem: 'Simplify §\\sqrt{1728}§ quickly',
              solution: [
                'Recognize §1728 = 12^3§ (from memorized cubes)',
                '§\\sqrt{1728} = \\sqrt{12^3} = \\sqrt{12^2 × 12}§',
                '§= 12\\sqrt{12} = 12\\sqrt{4 × 3} = 12 × 2\\sqrt{3}§',
                '§= 24\\sqrt{3}§ ✓',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'algebraic-shortcuts',
      title: 'Algebraic Identities',
      content: {
        introduction:
          'Algebraic identities are powerful tools for simplifying complex expressions and performing quick calculations. Master these formulas!',

        concepts: [
          {
            title: 'Essential Algebraic Formulas',
            explanation:
              'The three fundamental identities are:\n\n• §(a + b)^2 = a^2 + 2ab + b^2§\n• §(a - b)^2 = a^2 - 2ab + b^2§\n• §a^2 - b^2 = (a + b)(a - b)§\n\nThese can be applied to:\n• Calculate squares of any number\n• Factor expressions quickly\n• Simplify complex multiplications\n• Solve Quantitative Comparison problems\n\nCube formulas:\n• §(a + b)^3 = a^3 + 3a^2b + 3ab^2 + b^3§\n• §a^3 + b^3 = (a + b)(a^2 - ab + b^2)§\n• §a^3 - b^3 = (a - b)(a^2 + ab + b^2)§',
            visual: <AlgebraShortcutsVisual />,
            example: {
              problem: 'Calculate §103 × 97§ using algebraic identity',
              solution: [
                'Recognize: §103 × 97 = (100 + 3)(100 - 3)§',
                'This is the form §(a + b)(a - b) = a^2 - b^2§',
                '§= 100^2 - 3^2§',
                '§= 10000 - 9§',
                '§= 9991§ ✓',
              ],
            },
          },
        ],
      },
    },
  ];

  const practiceQuestions = [
    // Timed Practice
    {
      type: 'timed',
      timeLimit: 60,
      question: 'Calculate §45 × 11§ using mental math',
      options: ['§495§', '§455§', '§545§', '§485§', '§505§'],
      correct: 0,
      explanation:
        'Using the ×11 trick: Split §45§ into §4\\_5§, add middle digits §4 + 5 = 9§, place in middle: §495§.',
    },
    {
      type: 'timed',
      timeLimit: 60,
      question: 'What is §75^2§?',
      options: ['§5625§', '§5525§', '§5725§', '§5650§', '§5575§'],
      correct: 0,
      explanation:
        'For numbers ending in 5: §n × (n+1)§, then §25§. Here: §7 × 8 = 56§, append §25§: §5625§.',
    },
    {
      type: 'timed',
      timeLimit: 60,
      question: 'Calculate §48 × 5§ quickly',
      options: ['§240§', '§250§', '§235§', '§245§', '§230§'],
      correct: 0,
      explanation:
        'Using the ×5 trick: divide by 2, multiply by 10. §48 ÷ 2 = 24§, then §24 × 10 = 240§.',
    },
    {
      type: 'timed',
      timeLimit: 75,
      question: 'What is §52^2 - 48^2§?',
      options: ['§400§', '§200§', '§300§', '§500§', '§600§'],
      correct: 0,
      explanation:
        'Using §a^2 - b^2 = (a+b)(a-b)§: §(52+48)(52-48) = 100 × 4 = 400§.',
    },
    {
      type: 'timed',
      timeLimit: 60,
      question: '§36 × 25 = ?§',
      options: ['§900§', '§850§', '§925§', '§875§', '§950§'],
      correct: 0,
      explanation:
        'Using the ×25 trick: divide by 4, multiply by 100. §36 ÷ 4 = 9§, then §9 × 100 = 900§.',
    },
    {
      type: 'timed',
      timeLimit: 75,
      question: 'What is §15\\%§ of §80§?',
      options: ['§12§', '§10§', '§14§', '§8§', '§15§'],
      correct: 0,
      explanation:
        'Break into §10\\% + 5\\%§: §10\\%§ of §80 = 8§, §5\\%§ of §80 = 4§. Total: §8 + 4 = 12§.',
    },
    {
      type: 'timed',
      timeLimit: 60,
      question: 'Calculate §63 × 9§ using the ×9 trick',
      options: ['§567§', '§557§', '§577§', '§547§', '§587§'],
      correct: 0,
      explanation:
        'Using §×9 = ×10 - 1§: §63 × 10 = 630§, then §630 - 63 = 567§.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question: 'Estimate §\\sqrt{180}§',
      options: ['§13.4§', '§12.4§', '§14.4§', '§11.4§', '§15.4§'],
      correct: 0,
      explanation:
        '§\\sqrt{169} = 13§, §\\sqrt{196} = 14§. Since §180§ is closer to §169§, §\\sqrt{180} ≈ 13.4§.',
    },
    {
      type: 'timed',
      timeLimit: 60,
      question: '§320 ÷ 5 = ?§',
      options: ['§64§', '§62§', '§66§', '§68§', '§60§'],
      correct: 0,
      explanation:
        'Using ÷5 trick: multiply by 2, divide by 10. §320 × 2 = 640§, then §640 ÷ 10 = 64§.',
    },
    {
      type: 'timed',
      timeLimit: 75,
      question: 'What is §2/5§ as a percentage?',
      options: ['§40\\%§', '§25\\%§', '§45\\%§', '§35\\%§', '§50\\%§'],
      correct: 0,
      explanation:
        '§2/5 = 2 × 20/100 = 40/100 = 40\\%§. Alternatively, §1/5 = 20\\%§, so §2/5 = 40\\%§.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question: 'Calculate §97^2§',
      options: ['§9409§', '§9401§', '§9419§', '§9391§', '§9429§'],
      correct: 0,
      explanation:
        'Using §(100-3)^2§: §100^2 - 2(100)(3) + 3^2 = 10000 - 600 + 9 = 9409§.',
    },
    {
      type: 'timed',
      timeLimit: 60,
      question: '§72 × 11 = ?§',
      options: ['§792§', '§782§', '§772§', '§802§', '§812§'],
      correct: 0,
      explanation:
        'Using ×11 trick: §7\\_2§, middle = §7 + 2 = 9§, result: §792§.',
    },
    {
      type: 'timed',
      timeLimit: 75,
      question: '§450 ÷ 25 = ?§',
      options: ['§18§', '§16§', '§20§', '§15§', '§22§'],
      correct: 0,
      explanation:
        'Using ÷25 trick: multiply by 4, divide by 100. §450 × 4 = 1800§, then §1800 ÷ 100 = 18§.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question: 'What is §33\\%§ of §60§ (approximate)?',
      options: ['§20§', '§18§', '§22§', '§24§', '§16§'],
      correct: 0,
      explanation: '§33\\% ≈ 1/3§. One-third of §60 = 60 ÷ 3 = 20§.',
    },
    {
      type: 'timed',
      timeLimit: 60,
      question: '§2^8 = ?§',
      options: ['§256§', '§128§', '§512§', '§64§', '§324§'],
      correct: 0,
      explanation:
        'Powers of 2: §2^8 = 256§. (§2^1=2, 2^2=4, 2^3=8, 2^4=16, 2^5=32, 2^6=64, 2^7=128, 2^8=256§)',
    },
    // Untimed Practice
    {
      type: 'untimed',
      question: 'Calculate §78 × 11§',
      options: ['§858§', '§848§', '§868§', '§878§', '§838§'],
      correct: 0,
      explanation:
        'Using ×11 trick: §7\\_8§, middle = §7 + 8 = 15§. Since §15 > 9§, carry 1: §(7+1)58 = 858§.',
    },
    {
      type: 'untimed',
      question: 'What is §85^2§?',
      options: ['§7225§', '§7125§', '§7325§', '§7025§', '§7425§'],
      correct: 0,
      explanation: 'Numbers ending in 5: §8 × 9 = 72§, append §25§: §7225§.',
    },
    {
      type: 'untimed',
      question: 'Calculate §125 × 8§',
      options: ['§1000§', '§900§', '§1100§', '§800§', '§950§'],
      correct: 0,
      explanation:
        '§125 = 1000/8§, so §125 × 8 = 1000§. Or: §125 × 8 = 125 × 2^3 = 250 × 4 = 1000§.',
    },
    {
      type: 'untimed',
      question: 'What is §3/8§ as a percentage?',
      options: ['§37.5\\%§', '§35\\%§', '§40\\%§', '§33.3\\%§', '§38\\%§'],
      correct: 0,
      explanation: '§1/8 = 12.5\\%§, so §3/8 = 3 × 12.5\\% = 37.5\\%§.',
    },
    {
      type: 'untimed',
      question: 'Calculate §102 × 98§',
      options: ['§9996§', '§10000§', '§9986§', '§10004§', '§9976§'],
      correct: 0,
      explanation:
        'Using §(a+b)(a-b) = a^2 - b^2§: §(100+2)(100-2) = 100^2 - 2^2 = 10000 - 4 = 9996§.',
    },
    {
      type: 'untimed',
      question: 'What is §\\sqrt{576}§?',
      options: ['§24§', '§22§', '§26§', '§28§', '§23§'],
      correct: 0,
      explanation:
        'Recognize that §576 = 24^2§ (from memorized squares). Therefore, §\\sqrt{576} = 24§.',
    },
    {
      type: 'untimed',
      question: '§67 × 9 = ?§',
      options: ['§603§', '§593§', '§613§', '§583§', '§623§'],
      correct: 0,
      explanation:
        'Using §×9 = ×10 - 1§: §67 × 10 = 670§, then §670 - 67 = 603§.',
    },
    {
      type: 'untimed',
      question: 'Calculate §28^2§ using §(30-2)^2§',
      options: ['§784§', '§764§', '§794§', '§774§', '§804§'],
      correct: 0,
      explanation: '§(30-2)^2 = 30^2 - 2(30)(2) + 2^2 = 900 - 120 + 4 = 784§.',
    },
    {
      type: 'untimed',
      question: 'What percentage is §5/6§?',
      options: ['§83.3\\%§', '§80\\%§', '§85\\%§', '§75\\%§', '§87.5\\%§'],
      correct: 0,
      explanation:
        '§5/6 = 5 × (1/6) = 5 × 16.67\\% ≈ 83.3\\%§. Or: §5/6 = 1 - 1/6 ≈ 100\\% - 16.7\\% = 83.3\\%§.',
    },
    {
      type: 'untimed',
      question: '§5^3 = ?§',
      options: ['§125§', '§75§', '§100§', '§150§', '§175§'],
      correct: 0,
      explanation: '§5^3 = 5 × 5 × 5 = 25 × 5 = 125§.',
    },
    {
      type: 'untimed',
      question: 'Calculate §176 ÷ 8§',
      options: ['§22§', '§24§', '§20§', '§21§', '§23§'],
      correct: 0,
      explanation:
        'Using ÷8 = ÷2 ÷2 ÷2: §176 ÷ 2 = 88§, §88 ÷ 2 = 44§, §44 ÷ 2 = 22§.',
    },
    {
      type: 'untimed',
      question: 'What is §45\\%§ of §200§?',
      options: ['§90§', '§85§', '§95§', '§80§', '§100§'],
      correct: 0,
      explanation:
        '§45\\% = 50\\% - 5\\%§. §50\\%§ of §200 = 100§, §5\\%§ of §200 = 10§. So §45\\% = 100 - 10 = 90§.',
    },
    {
      type: 'untimed',
      question: 'Calculate §55^2§',
      options: ['§3025§', '§2925§', '§3125§', '§3075§', '§2975§'],
      correct: 0,
      explanation: 'Numbers ending in 5: §5 × 6 = 30§, append §25§: §3025§.',
    },
    {
      type: 'untimed',
      question: 'Estimate §47 × 53§',
      options: ['~§2500§', '~§2000§', '~§3000§', '~§2700§', '~§2200§'],
      correct: 0,
      explanation:
        '§47 × 53 = (50-3)(50+3) = 50^2 - 3^2 = 2500 - 9 = 2491 ≈ 2500§.',
    },
    {
      type: 'untimed',
      question: '§2^{10} = ?§',
      options: ['§1024§', '§1000§', '§512§', '§2048§', '§1048§'],
      correct: 0,
      explanation:
        '§2^{10} = 1024§. This is a key number to memorize (also 1 KB in computing).',
    },
    {
      type: 'untimed',
      questionType: 'quantitative_comparison',
      question: 'Compare the two quantities without long calculation.',
      quantityA: '§49 * 51§',
      quantityB: '§2500§',
      correct: 1,
      explanation:
        'Compare the two quantities directly. Quantity B is greater.',
    },
    {
      type: 'untimed',
      questionType: 'multiple_choice_multi',
      question: 'Which shortcuts are valid? Select all that apply.',
      options: [
        '§(a+b)^2 = a^2 + 2ab + b^2§',
        '§25% = 1/4§',
        '§x/0 = 0§',
        '§a^2 - b^2 = (a-b)(a+b)§',
        'Rounding can estimate before exact work',
      ],
      correct: [0, 1, 3, 4],
      explanation:
        'The algebra identities, percent benchmark, and estimation strategy are valid. Division by zero is undefined.',
    },
    {
      type: 'timed',
      timeLimit: 105,
      questionType: 'numeric_entry',
      question: 'Enter §15%§ of 240.',
      correctAnswer: '36',
      acceptedAnswers: ['36'],
      explanation: '10% of 240 is 24 and 5% is 12, so 15% is 36.',
    },
  ];

  const quickReference = [
    '×11: Split digits, put sum in middle (§45×11 = 4(9)5 = 495§)',
    '×5: Divide by 2, then ×10 (§48×5 = 24×10 = 240§)',
    '×25: Divide by 4, then ×100 (§36×25 = 9×100 = 900§)',
    '×9: ×10 minus the number (§47×9 = 470-47 = 423§)',
    '÷5: ×2, then ÷10 (§230÷5 = 460÷10 = 46§)',
    '÷25: ×4, then ÷100',
    '§n5^2 = n(n+1)§ then 25 (§35^2 = 3×4=12§, so §1225§)',
    '§(a+b)(a-b) = a^2 - b^2§',
    '§1/3 ≈ 33.3\\%§, §2/3 ≈ 66.7\\%§',
    '§1/8 = 12.5\\%§, §3/8 = 37.5\\%§, §5/8 = 62.5\\%§',
  ];

  const getStartedWithProps = {
    title: 'Need help with Math shortcuts?',
    spaceName: 'Quant hacks',
    humanCoachTitle: 'Talk to a GRE coach',
    liwazaAITitle: 'Ask AI',
    communityTitle: 'Ask other students',
    showHumanCoach: true,
    showLiwazaAI: true,
    showCommunity: true,
    aifirstmessage:
      'I want to learn mental math shortcuts for the GRE Quantitative section',
    aicontext:
      'Master multiplication, division, squaring, percentages, and estimation techniques for faster GRE calculations',
  };

  return (
    <LessonTemplate
      lessonId="math-shortcuts"
      lessonTitle="Math Shortcuts & Mental Math"
      lessonIcon={<FiZap />}
      lessonDescription="Master calculation tricks to solve GRE problems faster"
      sections={sections}
      practiceQuestions={practiceQuestions}
      quickReference={quickReference}
      getStartedWithProps={getStartedWithProps}
      supplementalPracticeSlot={
        <QuantLessonPracticeSlot lessonId="math-shortcuts" />
      }
    />
  );
};

export default MathShortcutsLesson;
